import React, { useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import slugify from 'slugify'

// Components
import { Link, graphql } from 'gatsby'
import ReactPlayer from 'react-player'
import Layout from '../../components/LayoutHospitality'
import {
  CopyLGStyle,
  CopyStyle,
  HeaderStyle,
  LinkButton,
  LinkButtonWhite,
  SubheadLGStyle,
  SubheadSMStyle,
  SubheadStyle,
} from '../../components/UIHospitality'
import Clients from '../../components/Clients'
import Flickity from 'react-flickity-component'
import Opps from '../../components/Opportunities'
import CallToAction from '../../components/CallToAction'
import { GatsbyImage } from 'gatsby-plugin-image'
import Seo from '../../components/Seo'

// Assets
import linkedin from '../../images/linkedin.svg'
import arrow from '../../images/icons/footer-form-submit-arrow.svg'
import '../../styles/main.css'

const About = ({ location, data }) => {
  const pageData = data.contentfulAboutPage

  const carousel = useRef(null)
  const [activeDotIndex, setActiveDotIndex] = useState(0)

  useEffect(() => {
    carousel.current.flkty.on('change', (e) => {
      setActiveDotIndex(e)
    })
  }, [])

  const prevSlide = () => {
    carousel.current.flkty.previous()
  }

  const nextSlide = () => {
    carousel.current.flkty.next()
  }

  return (
    <Layout location={location}>
      <Seo
        title={pageData.metadataTitle || 'About'}
        description={pageData.metadataDescription}
        image={
          pageData.metadataImage
            ? `https:${pageData.metadataImage.file.url}`
            : undefined
        }
      />
      {/* Hero */}
      <section tw="bg-[#202C3A]">
        <div tw="max-w-screen-xl mx-auto">
          <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] h-[575px] relative)">
            {/* Content */}
            <div tw="mx-7 flex flex-col justify-center lg:(block mt-24 mx-0 mb-0 col-start-2 col-span-7)">
              <p
                css={[
                  HeaderStyle,
                  tw`mt-8 mb-8 text-[#C2E1EA] lg:(mb-16 mt-0)`,
                ]}
              >
                {pageData.heroHeadline.heroHeadline}
              </p>
              <p css={[CopyLGStyle, tw`max-h-full text-[#F1FAFD] lg:pr-24`]}>
                {pageData.heroSubhead.heroSubhead}
              </p>
              <div tw="mt-10 text-white	">
                <LinkButtonWhite destination="/hospitality/find-talent#book-a-demo">
                  Book a demo
                </LinkButtonWhite>
              </div>
            </div>
            {/* Image */}
            <div tw="lg:(col-start-9 col-span-6 relative h-full)">
              <GatsbyImage
                objectFit="contain"
                objectPosition={'left bottom'}
                tw="w-full lg:(w-full h-full absolute top-0 object-cover object-position[left])"
                image={pageData.heroImage.gatsbyImageData}
                alt={pageData.heroImage.filename}
              />
            </div>
          </div>
        </div>
      </section>

      {/* What we stand for */}
      <section tw="max-w-screen-xl mx-auto">
        <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] my-24)">
          {/* Image */}
          <div tw="flex items-center order-2 lg:(order-1 col-span-5)">
            <GatsbyImage
              tw="max-h-[444px]"
              image={pageData.heroImage.gatsbyImageData}
              alt={pageData.heroImage.filename}
            />
          </div>
          {/* Content */}
          <div tw="mx-7 my-8 order-1 lg:(order-2 mx-0 my-0 col-start-7 col-span-6)">
            <h2 css={[HeaderStyle, tw`mb-10 lg:(my-10)`]}>What We Stand For</h2>
            <p
              css={[CopyStyle]}
              dangerouslySetInnerHTML={{
                __html: pageData.section1Copy.section1Copy.replace(
                  '\n',
                  '<br/><br/>'
                ),
              }}
            ></p>
            <div tw="mt-10">
              <LinkButton destination="/hospitality/find-talent#book-a-demo">
                Book a demo
              </LinkButton>
            </div>
          </div>
        </div>
      </section>

      {/* Clients */}
      <Clients clients={pageData.clients} hospitality={true} bgBlue />

      {/* Team */}
      <section tw="pt-14 pb-7 lg:(py-28)">
        {/* CEO */}
        <div tw="relative mx-7 mb-6 lg:(max-w-screen-lg grid grid-cols-12 gap-6 mx-auto mb-16)">
          <h2
            css={[
              HeaderStyle,
              tw`text-left mb-10 col-span-full lg:text-center`,
            ]}
          >
            {pageData.section2Headline}
          </h2>
          {/* CEO */}
          <div tw="col-start-3 col-span-8 flex flex-col justify-center lg:(block)">
            <GatsbyImage
              tw="mb-4 lg:(w-1/2 float-left mr-4 mb-0)"
              image={pageData.section2Ceo.photo.gatsbyImageData}
              alt={pageData.section2Ceo.photo.filename}
            />
            <div tw="flex justify-between items-start h-auto">
              <h3 css={[SubheadLGStyle, tw`mb-2`]}>
                {pageData.section2Ceo.name}
              </h3>
              <a href={pageData.section2Ceo.linkedInUrl} tw="pl-4 pb-4">
                <img tw="w-4 h-4" src={linkedin} />
              </a>
            </div>
            <h4 css={[CopyLGStyle, tw`mb-8`]}>{pageData.section2Ceo.role}</h4>
            <p css={[CopyStyle]}>{pageData.section2Ceo.bio.bio}</p>
          </div>
        </div>
        {/* Team - */}
        <div tw="grid grid-cols-2 gap-6 mx-7 lg:(max-w-screen-lg grid-cols-12 mx-auto)">
          {pageData.section2TeamMembers &&
            pageData.section2TeamMembers.map((member, index) => (
              <div tw="col-span-1 lg:(col-span-3)">
                <GatsbyImage
                  tw="w-full mb-4"
                  image={member.photo.gatsbyImageData}
                  alt={member.name}
                  className="height-thumbnail"
                />
                <div tw="flex flex-wrap justify-between items-start h-auto mb-12">
                  <h3 css={[SubheadSMStyle, tw`order-1 mb-2 lg:mb-0.5`]}>
                    {member.name}
                  </h3>
                  <a href={member.linkedInUrl} tw="order-3 lg:order-2">
                    <img tw="w-4 h-4" src={linkedin} />
                  </a>
                  <h4 css={[CopyStyle, tw`order-2 mb-3 w-full lg:order-3`]}>
                    {member.role}
                  </h4>
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* Opportunities at Staffy */}
      {pageData.section3Opportunities && (
        <Opps
          image={pageData.section3Image}
          headline={pageData.section3Headline}
          subhead={pageData.section3Subhead.section3Subhead}
          careers={pageData.section3Opportunities}
        />
      )}

      {/* In the media */}
      {pageData.section4Blogs && (
        <section tw="overflow-x-hidden pt-6 pb-12 lg:(pt-12 pb-24)">
          {/* Header */}
          <div tw="w-full">
            <div tw="relative mx-7 lg:(max-w-screen-lg mx-auto)">
              <h2 css={[SubheadStyle, tw`mb-8`]}>
                {pageData.section4Headline}
              </h2>
            </div>
          </div>
          {/* Mobile Carousel Layout */}
          <div tw="relative mx-7 lg:(max-w-screen-lg mx-auto hidden)">
            <Flickity
              className="carousel"
              ref={carousel}
              options={{
                prevNextButtons: false,
                pageDots: false,
                resize: true,
                wrapAround: false,
              }}
            >
              {pageData.section4Blogs.map((slide, index) => {
                return (
                  <div key={`blog-post-${index}`} tw="w-full mx-4 lg:(w-1/3)">
                    <GatsbyImage
                      objectFit="cover"
                      tw="w-full max-h-[212px]"
                      image={slide.image.gatsbyImageData}
                      alt={slide.image.filename}
                      className="height-thumbnail"
                    />
                    <div tw="inline-block border border-black w-full py-7 px-8 lg:(w-1/3 py-14 px-16)">
                      <h2
                        css={[SubheadSMStyle, tw`mb-4`]}
                        className="text-thumbnail"
                      >
                        {slide.title}
                      </h2>
                      <p css={[CopyStyle, tw`mb-4`]} className="text-thumbnail">
                        {slide.description}
                      </p>
                      <Link
                        css={[CopyStyle, tw`flex items-center`]}
                        to={`/hospitality/blog/${slugify(slide.title).toLowerCase()}`}
                      >
                        Read <img src={arrow} tw="w-8 ml-3" />
                      </Link>
                    </div>
                  </div>
                )
              })}
            </Flickity>
            {/* Flickity controls */}
            <div tw="mt-10 lg:hidden">
              {/* Dots */}
              <div tw="w-[200px] mx-auto relative">
                {pageData.section4Blogs.map((slide, index) => (
                  <hr
                    key={`dot-${index}`}
                    css={[
                      tw`inline-block border-[#202C3A]`,
                      `width: calc(100% / ${pageData.section4Blogs.length})`,
                      activeDotIndex === index && tw`border-[#FF4D00]`,
                    ]}
                  />
                ))}
              </div>
              {/* Arrows */}
              <div tw="mt-2 flex justify-center">
                <button tw="mx-2" onClick={prevSlide}>
                  Prev
                </button>
                <button tw="mx-2" onClick={nextSlide}>
                  Next
                </button>
              </div>
            </div>
          </div>
          {/* Desktop Grid Layout */}
          <div tw="hidden lg:(max-w-screen-lg grid grid-cols-12 gap-6 mx-auto)">
            {pageData.section4Blogs.map((slide, index) => (
              <div key={`blog-post-${index}`} tw="lg:(col-span-4)">
                <GatsbyImage
                  objectFit="cover"
                  tw="w-full max-h-[212px]"
                  image={slide.image.gatsbyImageData}
                  alt={slide.image.filename}
                  className="height-thumbnail"
                />
                <div tw="border border-black w-full py-7 px-8 lg:(py-8 px-12)">
                  <h2
                    css={[SubheadSMStyle, tw`mb-4`]}
                    className="text-thumbnail"
                  >
                    {slide.title}
                  </h2>
                  <p css={[CopyStyle, tw`mb-4`]} className="text-thumbnail">
                    {slide.description}
                  </p>
                  <Link
                    css={[CopyStyle, tw`flex items-center`]}
                    to={`/hospitality/blog/${slugify(slide.title).toLowerCase()}`}
                  >
                    Read <img src={arrow} tw="w-8 ml-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          {/* Read more CTA */}
          <div tw="relative mx-7 lg:(max-w-screen-lg mx-auto)">
            <div tw="flex justify-center mt-8 lg:mt-16">
              <LinkButton destination="/hospitality/blog">
                {pageData.section4Cta}
              </LinkButton>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <CallToAction
        header={pageData.ctaHeadline}
        copy={pageData.ctaSubhead.ctaSubhead}
        ctas={[
          { copy: 'Start Earning', destination: '/hospitality/start-earning' },
          { copy: 'Find Talent', destination: '/hospitality/find-talent' },
        ]}
        image={pageData.ctaImage}
      />
    </Layout>
  )
}

export default About
export const pageQuery = graphql`
  {
    contentfulAboutPage(
      pageId: { 
        eq: "About Page"
      }, 
      spaceId: {
        eq: "ufz0zh9i8v45"
      }
    ) {
      heroHeadline {
        heroHeadline
      }
      heroSubhead {
        heroSubhead
      }
      heroImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      section1Headline
      section1Copy {
        section1Copy
      }
      clients {
        name
        logo {
          filename
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            width: 300
            quality: 100
          )
        }
      }
      section2Headline
      section2Ceo {
        name
        role
        bio {
          bio
        }
        linkedInUrl
        photo {
          filename
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            width: 512
            quality: 80
          )
        }
      }
      section2TeamMembers {
        name
        role
        linkedInUrl
        photo {
          filename
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            width: 512
            quality: 80
          )
        }
      }
      section3Image {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      section3Headline
      section3Subhead {
        section3Subhead
      }
      section3Opportunities {
        title
        location
        description {
          description
        }
        jobPostingUrl
      }
      section4Headline
      section4Blogs {
        title
        date
        authorName
        description
        tags
        image {
          filename
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            width: 300
            quality: 100
          )
        }
        content {
          raw
        }
      }
      section4Cta
      ctaHeadline
      ctaSubhead {
        ctaSubhead
      }
      ctaImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      metadataTitle
      metadataDescription
      metadataImage {
        file {
          url
        }
      }
    }
  }
`
